import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import useApiEp from "../hooks/useApiEp";
import VoiceCallLogsTable from "../components/voice/VoiceCallLogsTable";

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString();
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "launched":
    case "completed":
    case "success":
      return "success";
    case "failed_extraction":
    case "failed":
      return "error";
    case "in_progress":
      return "warning";
    default:
      return "default";
  }
};

const DetailRow = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {value || "N/A"}
    </Typography>
  </Grid>
);

const CampaignDetailsModal = ({ open, onClose, campaign }) => {
  const [page, setPage] = useState(1);
  const { data: listRes = {}, isLoading: lLoad } = useApiEp(
    `/voice/get-voice-report-hooks-list/${campaign?.campaignId}?page=${page}`,
    !!campaign?.campaignId
  );
  const list = listRes.data || [];
  if (!campaign) return null;

  const patchList = campaign.agentRows?.patchList || [];
  const settings = campaign.settings || {};
  const fallback = campaign.failedMessagesFallback || {};

  const hasSettings = Object.keys(settings).length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            Campaign: {campaign.campaignName}
          </Typography>
          <Chip
            label={campaign.status?.toUpperCase()}
            color={getStatusColor(campaign.status)}
            size="small"
            sx={{ fontWeight: "bold" }}
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Basic Info Section */}
        <Box mb={3}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            General Information
          </Typography>
          <Grid container spacing={2}>
            <DetailRow label="Campaign ID" value={campaign.campaignId} />
            <DetailRow label="Customer ID" value={campaign.customerId} />
            <DetailRow label="Base File ID" value={campaign.baseFileId} />
            <DetailRow
              label="Created At"
              value={formatDate(campaign.createdAt)}
            />
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Settings & Fallback Section */}
        {(hasSettings || fallback.enabled) && (
          <Box mb={3}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Campaign Settings & Fallback
            </Typography>

            {/* Fallback Config */}
            {fallback.enabled && (
              <Box mb={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Failed Messages Fallback
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`Channel: ${fallback.channel?.toUpperCase()}`}
                    size="small"
                    color="info"
                  />
                  {fallback.templateId && (
                    <Chip
                      label={`Template: ${fallback.templateId}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Box>
            )}

            {/* DTMF Settings */}
            {settings.dtmfSettings?.length > 0 && (
              <Box mb={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  DTMF Triggers
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "action.hover" }}>
                      <TableRow>
                        <TableCell>
                          <strong>Input</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Type</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Condition</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Target Campaign</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {settings.dtmfSettings.map((dtmf, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Chip label={dtmf.input} size="small" />
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {dtmf.type}
                          </TableCell>
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {dtmf.condition}
                          </TableCell>
                          <TableCell>{dtmf.campaign}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Range Settings (Combined WhatsApp & RCS for compactness) */}
            {(settings.rangeSettingsWhatsapp?.length > 0 ||
              settings.rangeSettingsRcs?.length > 0) && (
              <Box mb={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Duration Range Settings
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "action.hover" }}>
                      <TableRow>
                        <TableCell>
                          <strong>Channel</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Target Campaign</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Min Duration</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Max Duration</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {settings.rangeSettingsWhatsapp?.map((range, idx) => (
                        <TableRow key={`wa-${idx}`}>
                          <TableCell>
                            <Chip
                              label="WhatsApp"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{range.campaign}</TableCell>
                          <TableCell align="right">
                            {range.minDuration}s
                          </TableCell>
                          <TableCell align="right">
                            {range.maxDuration}s
                          </TableCell>
                        </TableRow>
                      ))}
                      {settings.rangeSettingsRcs?.map((range, idx) => (
                        <TableRow key={`rcs-${idx}`}>
                          <TableCell>
                            <Chip
                              label="RCS"
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{range.campaign}</TableCell>
                          <TableCell align="right">
                            {range.minDuration}s
                          </TableCell>
                          <TableCell align="right">
                            {range.maxDuration}s
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {(hasSettings || fallback.enabled) && <Divider sx={{ my: 2 }} />}

        {/* Extraction Status Section */}
        <Box mb={3}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Extraction Details
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <DetailRow
              label="Started At"
              value={formatDate(campaign.extractionStartedAt)}
            />
            <DetailRow
              label="Ended At"
              value={formatDate(campaign.extractionEndedAt)}
            />
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Extraction Status
              </Typography>
              <Chip
                label={campaign.extractionStatus
                  ?.replace("_", " ")
                  .toUpperCase()}
                color={getStatusColor(campaign.extractionStatus)}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Agent Routing Section */}
        <Box>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Agent Routing (Patch List)
          </Typography>
          {patchList.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "action.hover" }}>
                  <TableRow>
                    <TableCell>
                      <strong>Group ID</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Group Name</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>DTMF Input</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patchList.map((patch, index) => (
                    <TableRow key={index}>
                      <TableCell>{patch.groupId}</TableCell>
                      <TableCell>{patch.groupName}</TableCell>
                      <TableCell align="center">
                        <Chip label={patch.agentDtmf} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No agent routing data available.
            </Typography>
          )}
        </Box>
        <Box mt={1}>
          <VoiceCallLogsTable
            rows={list}
            loading={lLoad}
            page={page}
            totalPages={listRes.totalPages}
            totalItems={listRes.totalItems}
            onPageChange={(v) => setPage(v)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" disableElevation>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDetailsModal;
